import React, { useState, useEffect } from 'react';
import { User, Settings, Shield, AlertTriangle, CheckCircle, XCircle, Instagram, Facebook, Youtube, Video, MessageCircle, RefreshCw } from 'lucide-react';
import { useMediaAccountsLazyQuery, Platform, MediaAccountsQuery, useRemoveMediaAccountMutation, useValidateAccountConnectionMutation } from 'graphql/generated/graphql';

interface Account {
  id: string;
  accountId?: number;
  platform: string;
  platformType: Platform;
  username?: string;
  nickname?: string | null;
  connected: boolean;
  icon: React.ComponentType<any>;
  color: string;
  lastSync?: string;
  igUserId?: string | null;
  privacyLevelOptions?: string[] | null;
  maxVideoDuration?: number | null;
  commentsDisabled?: boolean | null;
  duetDisabled?: boolean | null;
  stitchDisabled?: boolean | null;
}

const ConnectedAccountsPage = () => {
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState<number | null>(null);
  const [syncingAccount, setSyncingAccount] = useState<number | null>(null);
  const [validateConnection] = useValidateAccountConnectionMutation();


  // Platform configuration with icons and colors
  const platformConfig: Partial<Record<Platform, {
    icon: React.ComponentType<any>;
    color: string;
    name: string;
  }>> = {
    [Platform.Instagram]: {
      icon: Instagram,
      color: 'from-pink-500 to-purple-600',
      name: 'Instagram'
    },
    [Platform.Facebook]: {
      icon: Facebook,
      color: 'from-blue-600 to-blue-700',
      name: 'Facebook'
    },
    [Platform.Youtube]: {
      icon: Youtube,
      color: 'from-red-500 to-red-600',
      name: 'YouTube'
    },
    [Platform.TikTok]: {
      icon: Video,
      color: 'from-black to-gray-800',
      name: 'TikTok'
    },
    [Platform.Reddit]: {
      icon: MessageCircle,
      color: 'from-orange-500 to-red-500',
      name: 'Reddit'
    }
  };

  // Lazy queries for each platform
  const [getInstagramAccounts, { data: instagramData }] = useMediaAccountsLazyQuery();
  const [getFacebookAccounts, { data: facebookData }] = useMediaAccountsLazyQuery();
  const [getYoutubeAccounts, { data: youtubeData }] = useMediaAccountsLazyQuery();
  const [getTikTokAccounts, { data: tiktokData }] = useMediaAccountsLazyQuery();
  const [getRedditAccounts, { data: redditData }] = useMediaAccountsLazyQuery();

  // Disconnect mutation
  const [removeMediaAccount] = useRemoveMediaAccountMutation();

  // Extract fetchAllAccounts function so it can be reused
  const fetchAllAccounts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        getInstagramAccounts({ variables: { platform: Platform.Instagram } }),
        getFacebookAccounts({ variables: { platform: Platform.Facebook } }),
        getYoutubeAccounts({ variables: { platform: Platform.Youtube } }),
        getTikTokAccounts({ variables: { platform: Platform.TikTok } }),
        getRedditAccounts({ variables: { platform: Platform.Reddit } })
      ]);
    } catch (err: any) {
      setError(err?.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all platform accounts on component mount
  useEffect(() => {
    void fetchAllAccounts();
  }, [getInstagramAccounts, getFacebookAccounts, getYoutubeAccounts, getTikTokAccounts, getRedditAccounts]);

  // Aggregate all account data when queries complete
  useEffect(() => {
    const aggregatedAccounts = [];

    // Helper function to process platform data
    const processPlatformData = (data: MediaAccountsQuery | undefined, platform: Platform): Account[] => {
      if (data?.mediaAccounts) {
        return data.mediaAccounts.map(account => ({
          id: `${String(platform)}-${String(account.id)}`,
          accountId: account.id,
          platform: platformConfig[platform]?.name ?? String(platform),
          platformType: platform,
          username: account.accountName,
          nickname: account.nickname,
          connected: true,
          icon: platformConfig[platform]?.icon ?? Settings,
          color: platformConfig[platform]?.color ?? 'from-gray-500 to-gray-600',
          lastSync: 'Recently',
          igUserId: account.igUserId,
          privacyLevelOptions: account.privacy_level_options,
          maxVideoDuration: account.max_video_duration,
          commentsDisabled: account.comments_disabled,
          duetDisabled: account.duet_disabled,
          stitchDisabled: account.stitch_disabled
        }));
      }
      return [];
    };

    // Process each platform's data
    const instagram = processPlatformData(instagramData, Platform.Instagram);
    const facebook = processPlatformData(facebookData, Platform.Facebook);
    const youtube = processPlatformData(youtubeData, Platform.Youtube);
    const tiktok = processPlatformData(tiktokData, Platform.TikTok);
    const reddit = processPlatformData(redditData, Platform.Reddit);

    // Combine all accounts
    const connected = [...instagram, ...facebook, ...youtube, ...tiktok, ...reddit];

    // Add unconnected platforms (only supported ones)
    const supportedPlatforms = [
      Platform.Instagram,
      Platform.Facebook,
      Platform.Youtube,
      Platform.TikTok,
      Platform.Reddit
    ];
    const connectedPlatformTypes = connected.map(acc => acc.platformType);
    
    const unconnected = supportedPlatforms
      .filter(platform => !connectedPlatformTypes.includes(platform))
      .map(platform => ({
        id: platform,
        platform: platformConfig[platform]?.name ?? String(platform),
        platformType: platform,
        connected: false,
        icon: platformConfig[platform]?.icon ?? Settings,
        color: platformConfig[platform]?.color ?? 'from-gray-500 to-gray-600'
      }));

    setAllAccounts([...connected, ...unconnected]);
  }, [instagramData, facebookData, youtubeData, tiktokData, redditData]);

  const handleSync = async (accountId: number, platformType: Platform) => {
    setSyncingAccount(accountId);
    try {
      const result = await validateConnection({ 
        variables: { accountId } 
      });
      
      const status = result.data?.validateAccountConnection;
      
      if (status?.needsReconnection) {
        alert(`⚠️ ${status.message ?? 'Connection failed'}\n\nPlease disconnect and reconnect this account.`);
      } else if (status?.isValid) {
        alert(`✅ ${status.message ?? 'Connection validated successfully'}`);
      }
      
      // Refresh the accounts list to show any updates
      await fetchAllAccounts();
    } catch (err) {
      console.error('Validation failed:', err);
      alert('Failed to validate connection. Please try again.');
    } finally {
      setSyncingAccount(null);
    }
  };


  const handleDisconnect = async (accountId: number) => {
    try {
      const result = await removeMediaAccount({
        variables: { accountId }
      });

      if (result.data?.removeMediaAccount) {
        // Success - refresh all accounts to show updated state
        await fetchAllAccounts();
        setShowDisconnectModal(null);
        alert('Account disconnected successfully!');
      } else {
        alert('Failed to disconnect account. Please try again.');
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      alert('Error disconnecting account. Please try again.');
      setShowDisconnectModal(null);
    }
  };

  const handleConnect = (platform: Platform) => {
    // Redirect to your existing connect page
    window.location.href = '/connect';
  };

  const connectedAccounts = allAccounts.filter(acc => acc.connected);
  const connectedCount = connectedAccounts.length;
  const availableCount = allAccounts.filter(acc => !acc.connected).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading connected accounts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">Failed to load connected accounts</p>
          <p className="text-gray-400 text-sm mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Connected Accounts</h1>
              <p className="text-gray-400">Manage your social media connections</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-purple-500/20 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-300">{connectedCount}</div>
                <div className="text-sm text-gray-400">Connected Accounts</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{connectedCount}</div>
                <div className="text-sm text-gray-400">Ready to Use</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{availableCount}</div>
                <div className="text-sm text-gray-400">Available to Connect</div>
              </div>
            </div>
          </div>
        </div>

        {/* Accounts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {allAccounts.map((account) => {
            const IconComponent = account.icon;
            return (
              <div 
                key={account.id}
                className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-purple-500/20 p-6 hover:border-purple-400/40 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 bg-gradient-to-r ${account.color} rounded-xl`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">{account.platform}</h3>
                      {account.connected && (
                        <div>
                          <p className="text-gray-300">{account.username}</p>
                          {account.nickname && (
                            <p className="text-gray-400 text-sm">{account.nickname}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {account.connected ? (
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-sm text-green-400 font-medium">Connected</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <XCircle className="w-5 h-5 text-gray-500" />
                      <span className="text-sm text-gray-500 font-medium">Not Connected</span>
                    </div>
                  )}
                </div>

                {account.connected ? (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Account ID:</span>
                      <span className="text-gray-300">#{String(account.accountId)}</span>
                    </div>
                    {account.igUserId && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Instagram ID:</span>
                        <span className="text-gray-300">{account.igUserId}</span>
                      </div>
                    )}
                    
                    <div className="flex space-x-3 pt-3 border-t border-purple-500/20">
                      <button 
                        onClick={() => {
                          if (account.accountId) {
                            handleSync(account.accountId, account.platformType).catch(console.error);
                          }
                        }}
                        disabled={syncingAccount === account.accountId}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {syncingAccount === account.accountId ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>Syncing...</span>
                          </>
                        ) : (
                          <span>Sync Now</span>
                        )}
                      </button>
                      <button 
                        onClick={() => account.accountId && setShowDisconnectModal(account.accountId)}
                        className="px-4 py-2 bg-slate-700 text-gray-300 rounded-lg hover:bg-slate-600 hover:text-white transition-all duration-200 font-medium border border-slate-600"
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-gray-400 text-sm">
                      Connect your {account.platform} account to sync your content and analytics.
                    </p>
                    <button 
                      onClick={() => handleConnect(account.platformType)}
                      className="w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 shadow-lg font-medium"
                    >
                      Connect {account.platform}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Security Notice */}
        <div className="mt-8 bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-yellow-500/20 p-6">
          <div className="flex items-start space-x-4">
            <Shield className="w-6 h-6 text-yellow-400 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-400 mb-2">Security & Privacy</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Your account connections are encrypted and secure. We only access the data you explicitly authorize, 
                and you can disconnect any account at any time. All data syncing follows each platform&apos;s API guidelines 
                and privacy policies.
              </p>
              <button className="mt-3 text-purple-400 hover:text-purple-300 text-sm font-medium underline">
                Learn more about data privacy →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Disconnect Confirmation Modal */}
      {showDisconnectModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-xl shadow-2xl border border-purple-500/20 p-6 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-yellow-400" />
              <h3 className="text-xl font-semibold text-white">Disconnect Account?</h3>
            </div>
            
            <p className="text-gray-300 mb-6">
              Are you sure you want to disconnect this account? 
              This will stop syncing your content and analytics data.
            </p>
            
            <div className="flex space-x-3">
              <button 
                onClick={() => setShowDisconnectModal(null)}
                className="flex-1 px-4 py-2 bg-slate-700 text-gray-300 rounded-lg hover:bg-slate-600 hover:text-white transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  if (showDisconnectModal) {
                    handleDisconnect(showDisconnectModal).catch(console.error);
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 font-medium"
              >
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectedAccountsPage;